// src/context/NetworkContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { OfflineService } from '../services/OfflineService';

interface NetworkContextType {
  isOnline: boolean;
  offlineService: OfflineService;
  pendingOfflineChanges: boolean;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export const NetworkProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingOfflineChanges, setPendingOfflineChanges] = useState(false);
  const offlineService = React.useMemo(() => new OfflineService(), []);
  
  useEffect(() => {
    // Check for offline trades
    setPendingOfflineChanges(offlineService.hasOfflineTrades());
    
    const handleOnline = async () => {
      setIsOnline(true);
      
      // If we have offline changes, sync them
      if (offlineService.hasOfflineTrades()) {
        try {
          await offlineService.syncOfflineTrades();
          setPendingOfflineChanges(offlineService.hasOfflineTrades());
        } catch (error) {
          console.error('Failed to sync offline trades', error);
        }
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check for pending changes initially
    if (isOnline && offlineService.hasOfflineTrades()) {
      handleOnline();
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [offlineService]);
  
  return (
    <NetworkContext.Provider value={{ isOnline, offlineService, pendingOfflineChanges }}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = () => {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};